/* Progressive enhancement of the existing Ella modules and current Judge.me output. */
(() => {
  if (!document.body.classList.contains('template-index')) return;
  const mobile = matchMedia('(max-width:749px)');
  const reduced = matchMedia('(prefers-reduced-motion:reduce)');
  const cleanups = new WeakMap();
  const pendingReviews = new WeakMap();
  const behavior = () => reduced.matches ? 'auto' : 'smooth';
  function setupTrack(track, dots, reviews = false) {
    if (cleanups.has(track)) return;
    track.tabIndex = 0;
    track.setAttribute('aria-label', reviews ? 'Customer reviews' : 'Swipe or use arrow keys to browse');
    const cards = Array.from(track.children);
    if (!cards.length) return;
    // CSS flex layout determines the natural shared height. Measuring stretched
    // cards and writing parent height feeds back into ResizeObserver in the editor.
    if (reviews) track.style.removeProperty('height');
    let current = 0, frame = 0;
    const left = i => Math.max(0, Math.min(track.scrollWidth-track.clientWidth, cards[i].offsetLeft - cards[0].offsetLeft));
    const go = i => track.scrollTo({left:left(Math.max(0,Math.min(i,cards.length-1))),behavior:behavior()});
    const update = () => {
      frame=0;
      if (!track.isConnected) return;
      if(reviews)track.style.setProperty("--fv-review-card-width", (mobile.matches?track.clientWidth:Math.min(680,track.clientWidth*.58))+"px");
      let index=0, distance=Infinity;
      cards.forEach((card,i) => { const d=Math.abs(left(i)-track.scrollLeft); if(d<distance){index=i;distance=d;} });
      current=index;
      if (dots) Array.from(dots.children).forEach((button,i)=>button.setAttribute('aria-current',String(i===current)));
      if (reviews) {
        cards.forEach((card,i)=>{card.classList.toggle('is-current',i===current);card.inert=i!==current;card.setAttribute('aria-hidden',String(i!==current));});
      }
    };
    const scroll=()=>{if(!frame)frame=requestAnimationFrame(update);};
    track.addEventListener('scroll',scroll,{passive:true});
    track.addEventListener('keydown',event=>{
      if (event.target!==track || (!mobile.matches && !reviews)) return;
      const targets={ArrowRight:current+1,ArrowLeft:current-1,Home:0,End:cards.length-1};
      if(event.key in targets){event.preventDefault();go(targets[event.key]);}
    });
    if (dots) cards.forEach((card,i)=>{
      const button=document.createElement('button');button.type='button';
      button.setAttribute('aria-label',`Show review ${i+1} of ${cards.length}`);
      button.setAttribute('aria-controls',track.id);
      button.addEventListener('click',()=>go(i));
      button.addEventListener('keydown',event=>{
        const targets={ArrowRight:i+1,ArrowLeft:i-1,Home:0,End:cards.length-1};
        if(event.key in targets){event.preventDefault();const next=Math.max(0,Math.min(targets[event.key],cards.length-1));dots.children[next].focus();go(next);}
      });dots.append(button);
    });
    if(reviews){
      let drag=null;
      track.addEventListener('pointerdown',event=>{
        if(event.pointerType!=='mouse'||event.button!==0||event.target.closest('a,button'))return;
        drag={id:event.pointerId,x:event.clientX,left:track.scrollLeft,moved:false};
      });
      track.addEventListener('pointermove',event=>{
        if(!drag||event.pointerId!==drag.id)return;
        const delta=event.clientX-drag.x;
        if(!drag.moved&&Math.abs(delta)<6)return;
        if(!drag.moved){drag.moved=true;track.setPointerCapture(drag.id);track.style.scrollSnapType='none';track.style.userSelect='none';}
        event.preventDefault();track.scrollLeft=drag.left-delta;
      });
      const release=()=>{
        if(!drag)return;
        const moved=drag.moved;drag=null;track.style.removeProperty('scroll-snap-type');track.style.removeProperty('user-select');
        if(moved){update();go(current);}
      };
      track.addEventListener('pointerup',release);track.addEventListener('pointercancel',release);
      track.addEventListener('pointerleave',()=>{if(drag&&!drag.moved)drag=null;});
    }
    const reset=()=>{track.scrollTo({left:0,behavior:'auto'});update();};
    const resize=new ResizeObserver(scroll);resize.observe(track);cards.forEach(c=>resize.observe(c));
    mobile.addEventListener('change',reset);
    cleanups.set(track,()=>{resize.disconnect();mobile.removeEventListener('change',reset);cancelAnimationFrame(frame);});
    update();
  }
  function initTabs(root) {
    if(root.dataset.fvReady) return;root.dataset.fvReady='true';
    const tabs=Array.from(root.querySelectorAll('.tabs-component-panel-trigger'));
    const panels=Array.from(root.querySelectorAll('.tabs-component-content'));
    const tablist=tabs[0]?.parentElement;if(!tablist)return;
    tablist.setAttribute('role','tablist');tablist.setAttribute('aria-label','Featured Belts and New Arrivals');
    tabs.forEach((tab,i)=>{tab.id=`fv-tab-${panels[i].id}`;tab.setAttribute('role','tab');tab.setAttribute('aria-controls',panels[i].id);panels[i].setAttribute('role','tabpanel');panels[i].setAttribute('aria-labelledby',tab.id);});
    let activeTab=null;
    const sync=()=>{
      tabs.forEach((tab,i)=>{const active=tab.classList.contains('--active');tab.setAttribute('aria-selected',String(active));tab.tabIndex=active?0:-1;if(active&&activeTab!==tab){activeTab=tab;requestAnimationFrame(()=>panels[i].querySelectorAll('swiper-component').forEach(component=>component.initSwiper?.update()));}});
    };
    tabs.forEach((tab,i)=>tab.addEventListener('keydown',event=>{
      const targets={ArrowRight:i+1,ArrowLeft:i-1,Home:0,End:tabs.length-1};
      if(event.key in targets){event.preventDefault();const next=Math.max(0,Math.min(targets[event.key],tabs.length-1));tabs[next].click();tabs[next].focus();}
      if(event.key===' '){event.preventDefault();tab.click();}
    }));
    new MutationObserver(sync).observe(tablist,{attributes:true,subtree:true,attributeFilter:['class']});
    root.classList.add('fv-enhanced');sync();
  }
  function initReviews(root) {
    if(root.dataset.fvReady) return;
    const widget=root.querySelector('.jdgm-testimonials-carousel');
    const id=widget?.dataset.jdgmBlockId;
    const state=id && window.jdgmCarouselMode?.[id];
    const cards=widget && Array.from(widget.querySelectorAll('.jdgm-testimonial'));
    // Only current real app output is accepted; leave an unknown app version intact.
    if(!cards?.length || !state?.reviews?.length || cards.length!==state.reviews.length) return;
    root.dataset.fvReady='true';
    if(state.slideInterval){clearInterval(state.slideInterval);state.slideInterval=null;}
    const title=document.createElement('h2');title.className='fv-v19-review-title';title.textContent=widget.querySelector('.jdgm-title')?.textContent || 'What customers are saying';
    const track=document.createElement('div');track.className='fv-v19-review-track';track.id=`fv-reviews-${id}`;
    cards.forEach((card,i)=>{
      card.classList.remove('active');card.inert=false;card.removeAttribute('aria-hidden');
      const record=state.reviews[i];
      if(record.transparency_badges?.includes('review_collected_from_another_provider')){
        const origin=document.createElement('p');origin.className='fv-v19-review-origin';origin.textContent='Review imported from another source';card.append(origin);
      }
      // Keep the app's verified badge only when its live record explicitly supports it.
      if(record.verified_buyer!==true)card.querySelectorAll('.jdgm-reviewer-verified-icon').forEach(badge=>badge.remove());
      track.append(card);
    });
    const source=document.createElement('p');source.className='fv-v19-review-source';source.textContent='Reviews provided by Judge.me. See each product’s reviews for source and verification details.';
    widget.replaceWith(title,track,source);
    setupTrack(track,null,true);
  }
  function init(scope=document) {
    scope.querySelectorAll('.fv-v19-products').forEach(root=>{
      if(customElements.get('tabs-component'))initTabs(root);
      else customElements.whenDefined('tabs-component').then(()=>{if(root.isConnected)initTabs(root);});
    });
    scope.querySelectorAll('.fv-home__style-grid').forEach(track=>setupTrack(track));
    scope.querySelectorAll('[data-fv-reviews]').forEach(root=>{
      if(root.dataset.fvReady || pendingReviews.has(root))return;
      let frame=0,timer=0,attempts=0;
      const stop=()=>{observer.disconnect();cancelAnimationFrame(frame);clearTimeout(timer);pendingReviews.delete(root);};
      const check=()=>{
        frame=0;clearTimeout(timer);
        if(!root.isConnected){stop();return;}
        initReviews(root);
        if(root.dataset.fvReady){stop();return;}
        // App data can become ready without another DOM mutation. Retry briefly,
        // then rely on the observer for slow network responses; never poll forever.
        if(attempts++<240)timer=setTimeout(schedule,250);
      };
      const schedule=()=>{if(!frame)frame=requestAnimationFrame(check);};
      const observer=new MutationObserver(schedule);
      pendingReviews.set(root,stop);
      observer.observe(root,{childList:true,subtree:true});
      check();
    });
  }
  init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{event.target.querySelectorAll('[data-fv-reviews]').forEach(root=>pendingReviews.get(root)?.());event.target.querySelectorAll('[tabindex]').forEach(track=>cleanups.get(track)?.());});
  document.addEventListener('shopify:block:select',event=>{const card=event.target.closest('.fv-style-tile');if(card)card.scrollIntoView({block:'nearest',inline:'start',behavior:'auto'});});
})();
